import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'

import { DebitCardAccountUpdateSubscriptionResponse } from 'hooks/banking/useSubscribeToAccounts/graphql/DebitCardAccountUpdate.generated'
import { DebitCardAccountType } from 'types/gql-global-types'

import {
  AccountsListQuery,
  AccountsListQueryResponse,
} from '../useAccountsListQuery/graphql/AccountsListQuery.generated'

export const useCacheNewAccount = () => {
  const { cache } = useApolloClient()

  const updateApolloCache = useCallback(
    (
      accountUpdate: NonNullable<
        DebitCardAccountUpdateSubscriptionResponse['onDebitCardAccountUpdate']
      >
    ) => {
      const accountsListQuery = cache.readQuery<
        AccountsListQueryResponse,
        never
      >({
        query: AccountsListQuery,
      })

      const isDebitCardAccountUpdate =
        accountUpdate.type === DebitCardAccountType.ZLR_DEBIT

      const existingDebitCardAccounts =
        accountsListQuery?.getDebitCardAccountsV2.accounts

      const existingSavingsAccounts = accountsListQuery?.getSavingsAccounts

      const newAccount = {
        ...accountUpdate,
        __typename: 'DebitCardAccountV2' as const,
        cardsCount: {
          debit: 0,
          total: 0,
          expense: 0,
        },
      }

      if (
        isDebitCardAccountUpdate &&
        !existingDebitCardAccounts?.some(
          (cachedAccount) => cachedAccount?.id === accountUpdate.id
        )
      ) {
        cache.writeQuery<AccountsListQueryResponse, never>({
          query: AccountsListQuery,
          data: {
            getDebitCardAccountsV2: {
              accounts: [newAccount, ...(existingDebitCardAccounts || [])],
            },
            getSavingsAccounts: existingSavingsAccounts ?? [],
          },
        })
      }

      const isSavingsAccountUpdate =
        accountUpdate.type === DebitCardAccountType.SAVINGS

      if (
        isSavingsAccountUpdate &&
        !existingSavingsAccounts?.some(
          (cachedAccount) => cachedAccount?.id === accountUpdate.id
        )
      ) {
        cache.writeQuery<AccountsListQueryResponse, never>({
          query: AccountsListQuery,
          data: {
            getDebitCardAccountsV2: {
              accounts: existingDebitCardAccounts ?? [],
            },
            getSavingsAccounts: [
              newAccount,
              ...(existingSavingsAccounts || []),
            ],
          },
        })
      }
    },
    [cache]
  )

  return { cacheNewAccount: updateApolloCache }
}
